/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _3 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>3 - Операции spread и rest</h2>
    <p className={styles.description}>
      В современном JavaScript появилась новая операция для работы с итерируемыми объектами, такими как строка, массив
      или объект. Ее функционал и название, в отличии отсинтаксиса, зависит от места применения.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.1 - Операция spread</h3>
      <p className={styles.description}>
        <code className={styles.code}>...</code> - spread, распыление. Позволяет распылить коллекцию элементов в другую
        коллекцию, сделав копию оригинальной не изменяя ее. При этом, во время распыления, если элемент коллекции
        примитив - создается копия элемента, в случае сложного типа создается ссылка на оригинальный элемент.{' '}
      </p>
      <p className={styles.description}>Распыление можно использовать всего в 3-х ситуациях: </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Во время вызова функции, для передачи массива элементов отдельными аргументами
        </li>
        <li className={styles.list__item}>В литерале массива, при создании нового массив</li>
        <li className={styles.list__item}>В литерале объекта, при создании нового объекта</li>
      </ul>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.1.1 - Операция spread</h4>
        <p className={styles.description}>
          Представим ситуацию когда у нас есть массив температур за какой-то период, и необходимо программно найти самую
          маленькую температуру в массиве используя метод <code className={styles.code}>Math.min()</code>, который
          ожидает несколько аргументов, а не массив.{' '}
        </p>
        <p className={styles.description}>
          Если передать просто массив, получим <code className={styles.code}>NaN</code>, потому что{' '}
          <code className={styles.code}>Math.min()</code> не умеет работать с массивом.
        </p>
        <CopyBlock
          text={`const temperatures = [18, 14, 12, 21, 17, 29];

const min = Math.min(temperatures); // NaN`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          В данном случае нам поможет операция распыления, чтобы передать элементы массива отдельными аргументами при
          вызове функции.{' '}
        </p>
        <CopyBlock
          text={`const temperatures = [18, 14, 12, 21, 17, 29];

const min = Math.min(...temperatures); // 12`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          То есть запись <code className={styles.code}>Math.min(...[18, 14, 12, 21, 17, 29])</code> аналогична{' '}
          <code className={styles.code}>Math.min(18, 14, 12, 21, 17, 29)</code>, операция spread распылила массив
          элементов в аргументы функции.{' '}
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.1.2 - Распыление массивов</h4>
        <p className={styles.description}>
          При работе с массивами, можно скопировать элементы одного массива в другой. Массив{' '}
          <code className={styles.code}>copyOfHouses</code> будет независимой копией массива{' '}
          <code className={styles.code}>houses</code> и соответственно изменения в любом из них не отобразятся в другом.{' '}
        </p>
        <CopyBlock
          text={`const houses = ['Arryn', 'Frey', 'Greyjoy', 'Stark', 'Lannister', 'Tyrell'];
const copyOfHouses = [...houses];

console.log(houses); // ['Arryn','Frey','Greyjoy','Stark','Lannister','Tyrell']
console.log(copyOfHouses); // ['Arryn','Frey','Greyjoy','Stark','Lannister','Tyrell']
console.log(houses === copyOfHouses); // false - разные ссылки`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          То же самое можно было сделать используя метод <code className={styles.code}>slice()</code>, операция{' '}
          <code className={styles.code}>spread</code> просто позволяет записывать это более кратко, особенно когда
          распылений несколько.{' '}
        </p>
        <CopyBlock
          text={`const houses = ['Arryn', 'Frey', 'Greyjoy', 'Stark', 'Lannister', 'Tyrell'];
const slicedCopyOfHouses = houses.slice();

console.log(houses); // ['Arryn','Frey','Greyjoy','Stark','Lannister','Tyrell']
console.log(slicedCopyOfHouses); // ['Arryn','Frey','Greyjoy','Stark','Lannister','Tyrell']`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>При этом можно добавлять и удалять элементы при создании нового массива. </p>
        <CopyBlock
          text={`const houses = ['Arryn', 'Frey', 'Greyjoy', 'Stark', 'Lannister', 'Tyrell'];
const completeHouses = [...houses, 'Targaryen'];

console.log(houses); // ['Arryn','Frey','Greyjoy','Stark','Lannister','Tyrell']
console.log(completeHouses); // ['Arryn','Frey','Greyjoy','Stark','Lannister','Tyrell','Targaryen']`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Можно очень удобно соединять несколько массивов в один новый. Порядок распыления исходных массивов влияет на
          порядок элементов в результирующем массиве.{' '}
        </p>
        <CopyBlock
          text={`const firstBatch = ['Arryn', 'Frey', 'Greyjoy'];
const secondBatch = ['Stark', 'Lannister', 'Tyrell'];

const houses = [...firstBatch, ...secondBatch];

console.log(houses); // ['Arryn','Frey','Greyjoy','Stark','Lannister','Tyrell']`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Можно использовать классические методы массива вроде <code className={styles.code}>slice()</code> в комбинации
          с операцией <code className={styles.code}>spread</code>. Все кто смотрел сериал <b>Game of Thrones</b> знают,
          что никто кроме дома Ланистеров не платит по долгам. Сделаем массив содержащий все дома кроме Ланистеров.{' '}
        </p>
        <CopyBlock
          text={`const houses = ['Arryn', 'Frey', 'Greyjoy', 'Stark', 'Lannister', 'Tyrell'];
const housesInDebt = [...houses.slice(0, 4), ...houses.slice(5)];

console.log(housesInDebt); // ['Arryn','Frey','Greyjoy','Stark','Tyrell']`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Можно обновлять элементы не изменяя оригинальный массив, а создавая новый с обновленными значениями.{' '}
        </p>
        <CopyBlock
          text={`const houses = ['Arryn', 'Frey', 'Greyjoy', 'Stark', 'Lannister', 'Tyrell'];

// Вертикальная запись удобнее для чтения
const updatedHouses = [
  ...houses.slice(0, 1),
  'Frey of the Crossing',
  ...houses.slice(2),
];

console.log(updatedHouses);
// ['Arryn', 'Frey of the Crossing', 'Greyjoy', 'Stark', 'Lannister', 'Tyrell']`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.1.3 - Распыление объектов</h4>
        <p className={styles.description}>
          Для объектов, в будущих стандартах языка, будет так же утверждена операция{' '}
          <code className={styles.code}>spread</code>. А в современных браузерах, и используя такие инструменты как{' '}
          <b>Babel</b>, его можно применять уже сейчас. Он позволяет распылять свойства одного объекта в другой.{' '}
        </p>
        <p className={styles.description}>
          Распыление можно использовать как замену <code className={styles.code}>Object.assign()</code>, то есть когда
          необходимо создать новый объект или обновить уже существующий по результату слияния нескольких других
          объектов.{' '}
        </p>
        <CopyBlock
          text={`const a = { x: 1, y: 2 };
            const b = { x: 0, z: 3 };

            const c = Object.assign({}, a, b);
            console.log(c); // {x: 0, y: 2, z: 3}

// То же самое используя операцию spread
            const d = { ...a, ...b };

            console.log(d); // {x: 0, y: 2, z: 3}`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Во время слияния можно добавлять свойства в произвольное место. Но следует помнить, что свойства распыляемого
          объекта могут перезаписать значение существующего свойства, если имена ключей совпадают, а распыление
          происходит после указания свойства{' '}
        </p>
        <CopyBlock
          text={`const a = { x: 1, y: 2 };
const b = { x: 0, z: 3 };

const c = { x: 5, j: 10, ...a, z: 15, ...b };

console.log(c); // {x: 0, j: 10, y: 2, z: 3}`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>Создадим химеру :) </p>
        <CopyBlock
          text={`const lion = { hasTail: true, legs: 4 };
const eagle = { canFly: true };

const chimera = { ...lion, ...eagle };

console.log(chimera); // {hasTail: true, legs: 4, canFly: true}`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.2 - Операция rest</h3>
      <p className={styles.description}>
        <code className={styles.code}>...</code> - rest, сбор. Позволяет скопировать группу элементов коллекции в новую
        коллекцию, не изменяя оригинальную. При этом, во время сбора, если элемент коллекции примитив - создается копия
        элемента, в случае сложного типа создается ссылка на оригинальный элемент.
      </p>
      <p className={styles.description}>
        Используется в подписи функции для сбора аргументов или при деструктуризации, для хранения остатка элементов.{' '}
      </p>
      <p className={styles.notificationInfo}>
        Деструктуризация массивов и объектов описана в следующем разделе, а функции рассмотрим сейчас.{' '}
      </p>
      <p className={styles.description}>
        Есть функция <code className={styles.code}>add</code>, которая складывает произвольное кол-во аргументов.
        Получить доступ ко всем аргументам можно используя специальную переменную{' '}
        <code className={styles.code}>arguments</code>, но в стрелочных функциях ее нет, а в обычных функциях она
        псевдомассив. Используя операцию <code className={styles.code}>rest</code> можно собрать все аргументы в массив
        прямо в подписи функции.{' '}
      </p>
      <CopyBlock
        text={`const add = function(...args) {
  console.log(args); // массив всех аргументов
};

add(1, 2, 3);
add(1, 2, 3, 4, 5);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Теперь предположим что функция <code className={styles.code}>add</code> не просто считает сумму аргументов, а
        еще получает первым аргументом число, которое необходимо прибавлять к каждому последующему аргументу при
        вычислении суммы.
      </p>
      <p className={styles.description}>
        Используя <code className={styles.code}>rest</code> можно взять только ту часть аргументов, которая необходима,
        объявив параметры до <code className={styles.code}>rest</code>. Все агументы, для которых будут объявлены
        параметры, передадут им свои значения, все остальные будут помещены в массив{' '}
        <code className={styles.code}>args</code>.{' '}
      </p>
      <p className={styles.notificationWarn}>
        Операция <code className={styles.code}>rest</code> собирает все оставшиеся аргументы и поэтому должна идти
        последней в подписи функции, иначе будет ошибка.{' '}
      </p>
      <CopyBlock
        text={`const add = function(value, ...args) {
  console.log(value); // первый аргумент
  console.log(args); // массив всех остальных аргументов
};

add(10, 1, 2, 3);
add(15, 1, 2, 3, 4, 5);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.3 - Дополнительные материалы</h3>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://nitayneeman.com/posts/object-rest-and-spread-properties-in-ecmascript-2018/"
            target="_blank"
          >
            Object Rest/Spread Properties in ES2018
          </a>
        </li>
        <li className={styles.list__item}>
          <a className={styles.link} href="https://davidwalsh.name/spread-operator" target="_blank">
            Great Uses of the Spread Operator
          </a>
        </li>
      </ul>
    </section>
  </section>
))
